import {Box, Clickable, HStack, Text, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {useCallback, useState} from 'react';

import {append, isEmpty, without} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormErrorContext, SetFormErrorCallback} from '../../Form/FormErrorContext';
import {WizardStepConfig} from '../WizardStepConfig';
import {OffscreenContent} from './OffscreenContent';
import {WizardStepBadge} from './WizardStepBadge';

interface WizardProps extends WizardStepConfig, TestIdProps {
  isCurrent: boolean;
  /**
   * Whether this step currently has keyboard focus (used for roving tabindex pattern).
   * Platform API addition for accessibility improvements - manages focus state in wizard navigation.
   */
  isFocused?: boolean;
  position: number;
  onClick: (position: number) => void;
  borderPosition?: 'left' | 'top';
  isBold?: boolean;
}

const wizardStepVariants = cva(
  'h-[calc(var(--eag-space-step,0.25)*18rem)] w-full overflow-hidden border-solid px-6 py-4 focus-visible:[outline:calc(var(--eag-space-step,0.25)*1rem)_solid_transparent] focus-visible:[border-bottom:calc(var(--eag-space-step,0.25)*1rem)_solid_var(--color-general-accent)]',
  {
    variants: {
      borderPosition: {
        left: 'border-l-4',
        top: 'border-t-4',
      },
      isCurrent: {
        true: 'border-[var(--color-general-accent)] bg-[var(--color-general-white)] hover:bg-[var(--color-palettes-white-10-100)]',
        false:
          'border-[var(--color-general-transparent)] bg-[var(--color-general-transparent)] hover:bg-[var(--color-palettes-neutral-30-100)]',
      },
    },
  }
);

export function WizardStep(props: WizardProps) {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const formErrorContextValue = useCallback<SetFormErrorCallback>(
    (name, error) => setInvalidFields(isNilOrEmpty(error) ? without([name]) : append(name)),
    [setInvalidFields]
  );
  return (
    <>
      <Clickable
        isDisabled={props.isDisabled}
        onClick={() => props.onClick(props.position)}
        data-testid={suffixTestId(`wizardStep-[${props.position}]`, props)}
      >
        <div
          role="tab"
          aria-label={props.title}
          id={`wizard-tab-${props.position}`}
          aria-controls={props.isCurrent ? `wizard-tabpanel-${props.position}` : undefined}
          tabIndex={props.isDisabled ? -1 : props.isFocused || props.isCurrent ? 0 : -1} // eslint-disable-line no-nested-ternary -- Pre-existing violation
          data-wizard-tab-index={props.position}
          aria-current={props.isCurrent ? 'step' : undefined}
          aria-selected={props.isCurrent ? 'true' : 'false'}
          aria-disabled={props.isDisabled ? 'true' : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              event.stopPropagation();
              if (!props.isDisabled) {
                props.onClick(props.position);
              }
            }
          }}
          className={cn(
            wizardStepVariants({
              borderPosition: props.borderPosition ?? 'left',
              isCurrent: props.isCurrent,
            })
          )}
        >
          <HStack spacing={2}>
            <Box flexShrink={0}>
              <WizardStepBadge
                position={props.position}
                isCurrent={props.isCurrent}
                severity={isEmpty(invalidFields) ? props.severity : 'danger'}
                data-testid={props['data-testid']}
              />
            </Box>
            <VStack spacing={1} grow={1} minWidth={0}>
              <Text
                size="small"
                color={props.isDisabled ? 'tertiary' : 'primary'}
                role="heading" // eslint-disable-line jsx-a11y/role-has-required-aria-props -- Muted pre-existing violation
                alternative={props.isBold}
              >
                {props.title}
              </Text>
              <Box width="100%" minWidth={0}>
                <Text size="xSmall" color="tertiary" noWrap>
                  {props.subtitle}
                </Text>
              </Box>
            </VStack>
          </HStack>
        </div>
      </Clickable>
      <FormErrorContext.Provider value={formErrorContextValue}>
        <OffscreenContent>{props.content}</OffscreenContent>
      </FormErrorContext.Provider>
    </>
  );
}
